import { useState, useEffect } from 'react';
import { deletionRequestsAPI } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Trash2, Check, X, AlertTriangle, Clock, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const STATUS_CONFIG = {
    pending: { label: 'En attente', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
    approved: { label: 'Approuvée', color: 'bg-green-100 text-green-800', icon: CheckCircle },
    rejected: { label: 'Refusée', color: 'bg-red-100 text-red-800', icon: XCircle },
};

export function DeletionRequestsPanel() {
    const { user, isAdminFull, isAdmin } = useAuth();
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('pending');
    const [rejectDialog, setRejectDialog] = useState({ open: false, request: null });
    const [rejectReason, setRejectReason] = useState('');
    const [confirmDialog, setConfirmDialog] = useState({ open: false, request: null });
    const [processing, setProcessing] = useState(false);

    const canManage = isAdminFull() || isAdmin();

    useEffect(() => {
        loadRequests();
    }, [statusFilter]);

    const loadRequests = async () => {
        try {
            setLoading(true);
            const res = await deletionRequestsAPI.getAll(statusFilter || undefined);
            setRequests(res.data || []);
        } catch (error) {
            console.error('Error loading deletion requests:', error);
            toast.error('Erreur lors du chargement des demandes');
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = async () => {
        if (!confirmDialog.request) return;
        
        try {
            setProcessing(true);
            await deletionRequestsAPI.approve(confirmDialog.request.id);
            toast.success('Demande approuvée - Dossier supprimé définitivement');
            setConfirmDialog({ open: false, request: null });
            loadRequests();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de l\'approbation');
        } finally {
            setProcessing(false);
        }
    };

    const handleReject = async () => {
        if (!rejectDialog.request || !rejectReason.trim()) {
            toast.error('Veuillez indiquer une raison');
            return;
        }
        
        try {
            setProcessing(true);
            await deletionRequestsAPI.reject(rejectDialog.request.id, rejectReason);
            toast.success('Demande refusée');
            setRejectDialog({ open: false, request: null });
            setRejectReason('');
            loadRequests();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors du refus');
        } finally {
            setProcessing(false);
        }
    };

    const pendingCount = requests.filter(r => r.status === 'pending').length;

    return (
        <Card data-testid="deletion-requests-panel">
            <CardHeader>
                <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                        <Trash2 className="w-5 h-5 text-red-600" />
                        Demandes de suppression
                        {pendingCount > 0 && (
                            <Badge variant="destructive" className="ml-2">
                                {pendingCount} en attente
                            </Badge>
                        )}
                    </CardTitle>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger className="w-[180px]" data-testid="status-filter">
                            <SelectValue placeholder="Filtrer par statut" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Toutes</SelectItem>
                            <SelectItem value="pending">En attente</SelectItem>
                            <SelectItem value="approved">Approuvées</SelectItem>
                            <SelectItem value="rejected">Refusées</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </CardHeader>
            <CardContent>
                {loading ? (
                    <div className="text-center py-8 text-slate-500">Chargement...</div>
                ) : requests.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                        Aucune demande de suppression
                    </div>
                ) : (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Dossier</TableHead>
                                <TableHead>Demandeur</TableHead>
                                <TableHead>Motif</TableHead>
                                <TableHead>Date</TableHead>
                                <TableHead>Statut</TableHead>
                                {canManage && <TableHead className="text-right">Actions</TableHead>}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {requests.map((req) => {
                                const StatusIcon = STATUS_CONFIG[req.status]?.icon || Clock;
                                return (
                                    <TableRow key={req.id} data-testid={`deletion-request-${req.id}`}>
                                        <TableCell className="font-medium">
                                            {req.prospect_name}
                                        </TableCell>
                                        <TableCell>
                                            <div>
                                                <p className="text-sm">{req.requested_by_name}</p>
                                                <p className="text-xs text-slate-500 capitalize">{req.requested_by_role}</p>
                                            </div>
                                        </TableCell>
                                        <TableCell className="max-w-[200px]">
                                            <p className="text-sm truncate" title={req.motif}>
                                                {req.motif}
                                            </p>
                                        </TableCell>
                                        <TableCell className="text-sm text-slate-500">
                                            {format(new Date(req.created_at), 'dd/MM/yyyy HH:mm', { locale: fr })}
                                        </TableCell>
                                        <TableCell>
                                            <Badge className={STATUS_CONFIG[req.status]?.color}>
                                                <StatusIcon className="w-3 h-3 mr-1" />
                                                {STATUS_CONFIG[req.status]?.label}
                                            </Badge>
                                            {req.status === 'rejected' && req.rejection_reason && (
                                                <p className="text-xs text-red-600 mt-1" title={req.rejection_reason}>
                                                    Raison: {req.rejection_reason.substring(0, 30)}...
                                                </p>
                                            )}
                                        </TableCell>
                                        {canManage && (
                                            <TableCell className="text-right">
                                                {req.status === 'pending' && (
                                                    <div className="flex gap-2 justify-end">
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            className="text-green-600 border-green-300 hover:bg-green-50"
                                                            onClick={() => setConfirmDialog({ open: true, request: req })}
                                                            data-testid={`approve-btn-${req.id}`}
                                                        >
                                                            <Check className="w-4 h-4" />
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            className="text-red-600 border-red-300 hover:bg-red-50"
                                                            onClick={() => setRejectDialog({ open: true, request: req })}
                                                            data-testid={`reject-btn-${req.id}`}
                                                        >
                                                            <X className="w-4 h-4" />
                                                        </Button>
                                                    </div>
                                                )}
                                                {req.status !== 'pending' && req.processed_by_name && (
                                                    <p className="text-xs text-slate-500">
                                                        Par {req.processed_by_name}
                                                    </p>
                                                )}
                                            </TableCell>
                                        )}
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                )}
            </CardContent>

            {/* Dialog de confirmation d'approbation */}
            <Dialog open={confirmDialog.open} onOpenChange={(open) => !open && setConfirmDialog({ open: false, request: null })}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-600">
                            <AlertTriangle className="w-5 h-5" />
                            Confirmer la suppression définitive
                        </DialogTitle>
                        <DialogDescription className="pt-4">
                            <p className="mb-2">
                                Vous êtes sur le point de supprimer <strong>définitivement</strong> le dossier :
                            </p>
                            <p className="font-semibold text-lg text-slate-900 mb-4">
                                {confirmDialog.request?.prospect_name}
                            </p>
                            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-800 text-sm">
                                <p className="font-medium">Attention !</p>
                                <ul className="list-disc list-inside mt-1 space-y-1">
                                    <li>Cette action est irréversible</li>
                                    <li>Toutes les données associées seront supprimées</li>
                                    <li>Les commissions liées seront également supprimées</li>
                                </ul>
                            </div>
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="gap-2">
                        <Button
                            variant="outline"
                            onClick={() => setConfirmDialog({ open: false, request: null })}
                            disabled={processing}
                        >
                            Annuler
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleApprove}
                            disabled={processing}
                            data-testid="confirm-delete-btn"
                        >
                            {processing ? 'Suppression...' : 'Supprimer définitivement'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Dialog de refus */}
            <Dialog open={rejectDialog.open} onOpenChange={(open) => !open && setRejectDialog({ open: false, request: null })}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Refuser la demande</DialogTitle>
                        <DialogDescription>
                            Indiquez la raison du refus pour le dossier : <strong>{rejectDialog.request?.prospect_name}</strong>
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-4">
                        <Input
                            placeholder="Raison du refus (obligatoire)"
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                            data-testid="reject-reason-input"
                        />
                    </div>
                    <DialogFooter className="gap-2">
                        <Button
                            variant="outline"
                            onClick={() => {
                                setRejectDialog({ open: false, request: null });
                                setRejectReason('');
                            }}
                            disabled={processing}
                        >
                            Annuler
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleReject}
                            disabled={processing || !rejectReason.trim()}
                            data-testid="confirm-reject-btn"
                        >
                            {processing ? 'Refus...' : 'Refuser'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Card>
    );
}

export default DeletionRequestsPanel;
